import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { initiateRazorpayPayment } from '../services/razorpayService';
import '../styles/Checkout.css';

const Checkout = () => {
  const { cart, getTotal, placeOrder } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [shippingInfo, setShippingInfo] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    landmark: ''
  });

  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [loading, setLoading] = useState(false);
  const [tip, setTip] = useState(0);
  const [customTip, setCustomTip] = useState('');

  // Calculate subtotal first
  const subtotal = getTotal();
  const sgst = subtotal * 0.025; // 2.5% SGST
  const cgst = subtotal * 0.025; // 2.5% CGST
  const totalTax = sgst + cgst; // 5% total GST
  const deliveryCharge = 0; // Free delivery within 10km radius
  const MINIMUM_ORDER_AMOUNT = 150;

  const total = subtotal + totalTax + deliveryCharge + tip;

  const handleInputChange = (e) => {
    setShippingInfo({
      ...shippingInfo,
      [e.target.name]: e.target.value
    });
  };

  const handleTipSelect = (amount) => {
    setTip(amount);
    setCustomTip('');
  };

  const handleCustomTipChange = (e) => {
    const value = e.target.value;
    setCustomTip(value);
    const numValue = parseFloat(value) || 0;
    setTip(numValue);
  };

  const validateForm = () => {
    const required = ['fullName', 'email', 'phone', 'address', 'city', 'state', 'pincode'];
    for (let field of required) {
      if (!shippingInfo[field].trim()) {
        alert(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shippingInfo.email)) {
      alert('Please enter a valid email address');
      return false;
    }
    
    // Validate phone
    if (shippingInfo.phone.length < 10) {
      alert('Please enter a valid phone number');
      return false;
    }
    
    // Validate pincode
    if (shippingInfo.pincode.length !== 6) {
      alert('Please enter a valid 6-digit pincode');
      return false;
    }
    
    return true;
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    const orderData = {
      items: cart,
      shippingInfo,
      paymentMethod,
      subtotal,
      sgst,
      cgst,
      totalTax,
      deliveryCharge: 0,
      tip,
      total,
      userEmail: user.email,
      userName: shippingInfo.fullName
    };

    // If online payment selected, initiate Razorpay
    if (paymentMethod === 'online') {
      try {
        await initiateRazorpayPayment(
          orderData,
          // On payment success
          async (paymentResponse) => {
            try {
              // Add payment details to order
              const orderWithPayment = {
                ...orderData,
                paymentStatus: 'paid',
                paymentId: paymentResponse.razorpay_payment_id,
                paymentDetails: paymentResponse
              };
              
              console.log('Checkout: Placing order with payment:', orderWithPayment);
              await placeOrder(user.email, orderWithPayment);
              alert('Payment successful! Order placed.');
              
              setTimeout(() => {
                navigate('/orders');
              }, 1000);
            } catch (error) {
              console.error('Error saving order after payment:', error);
              alert('Payment successful but error saving order. Please contact support with payment ID: ' + paymentResponse.razorpay_payment_id);
            } finally {
              setLoading(false);
            }
          },
          // On payment failure
          (error) => {
            console.error('Payment failed:', error);
            alert('Payment failed: ' + error);
            setLoading(false);
          }
        );
      } catch (error) {
        console.error('Error initiating payment:', error);
        alert('Error initiating payment. Please try again.');
        setLoading(false);
      }
    } else {
      // Cash on Delivery
      try {
        const orderWithPayment = {
          ...orderData,
          paymentStatus: 'pending',
          paymentId: null
        };
        
        console.log('Checkout: Placing COD order:', orderWithPayment);
        await placeOrder(user.email, orderWithPayment);
        alert('Order placed successfully!');
        
        setTimeout(() => {
          navigate('/orders');
        }, 1000);
      } catch (error) {
        console.error('Error placing order:', error);
        alert('Error placing order. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  if (cart.length === 0) {
    return (
      <div className="checkout">
        <div className="empty-checkout">
          <h2>Your cart is empty</h2>
          <button onClick={() => navigate('/shop')} className="btn-shop">
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout">
      <div className="checkout-container">
        <h1>Checkout</h1>
        
        <div className="checkout-content">
          <div className="checkout-left">
            {/* Shop Location Info */}
            <div className="checkout-section">
              <h2>Delivery Area - Important</h2>
              <div className="shop-location">
                <div className="location-info">
                  <span className="location-icon">📍</span>
                  <div className="location-details">
                    <strong>MD Sweets Caterers</strong>
                    <p>Chomu, Jaipur, Rajasthan</p>
                  </div>
                </div>
                <div className="delivery-info-simple">
                  <h4>⚠️ Delivery Policy:</h4>
                  <p className="delivery-area-notice">
                    <strong>We deliver in Chomu area only</strong>
                  </p>
                  <p>✅ Free delivery within Chomu</p>
                  <p>🕐 Delivery timings: 9:00 AM to 5:00 PM</p>
                  <p>❌ Outside Chomu? Please use <a href="/custom-order" style={{color: '#667eea', fontWeight: 'bold'}}>Custom Order</a> section</p>
                  <p style={{fontSize: '0.85rem', marginTop: '0.5rem', color: '#666'}}>
                    For bulk orders or special requests outside Chomu, visit our Custom Order page
                  </p>
                </div>
              </div>
            </div>

            {/* Shipping Information */}
            <div className="checkout-section">
              <h2>Shipping Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={shippingInfo.fullName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={shippingInfo.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={shippingInfo.phone}
                    onChange={handleInputChange}
                    placeholder="10-digit mobile number"
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label>Address *</label>
                  <textarea
                    name="address"
                    value={shippingInfo.address}
                    onChange={handleInputChange}
                    placeholder="House/Flat no, Building, Street"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="city"
                    value={shippingInfo.city}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    name="state"
                    value={shippingInfo.state}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    value={shippingInfo.pincode}
                    onChange={handleInputChange}
                    placeholder="6-digit pincode"
                    maxLength="6"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Landmark (Optional)</label>
                  <input
                    type="text"
                    name="landmark"
                    value={shippingInfo.landmark}
                    onChange={handleInputChange}
                    placeholder="Near famous place"
                  />
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="checkout-section">
              <h2>Payment Method</h2>
              <div className="payment-methods">
                <label className="payment-method">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="cod"
                    checked={paymentMethod === 'cod'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <div className="payment-info">
                    <span>Cash on Delivery</span>
                    <small>Pay when you receive your order</small>
                  </div>
                </label>
                <label className="payment-method">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="online"
                    checked={paymentMethod === 'online'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <div className="payment-info">
                    <span>Online Payment</span>
                    <small>UPI, Cards, Net Banking</small>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="checkout-right">
            {/* Order Summary */}
            <div className="order-summary">
              <h2>Order Summary</h2>
              <div className="order-items">
                {cart.map(item => (
                  <div key={item.cartId} className="order-item">
                    <img src={item.image} alt={item.name} />
                    <div className="item-details">
                      <h4>{item.displayName || item.name}</h4>
                      {item.variant && (
                        <p className="item-variant">{item.variant.name}</p>
                      )}
                      <p className="item-quantity">Qty: {item.quantity}</p>
                    </div>
                    <div className="item-price">
                      ₹{(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Tip Section */}
              <div className="tip-section">
                <h3>💝 Support Our Delivery Staff (Optional)</h3>
                <p className="tip-description">Your tip goes directly to our delivery team</p>
                <div className="tip-options">
                  <button 
                    className={`tip-btn ${tip === 0 && !customTip ? 'active' : ''}`}
                    onClick={() => handleTipSelect(0)}
                  >
                    No Tip
                  </button>
                  <button 
                    className={`tip-btn ${tip === 10 ? 'active' : ''}`}
                    onClick={() => handleTipSelect(10)}
                  >
                    ₹10
                  </button>
                  <button 
                    className={`tip-btn ${tip === 20 ? 'active' : ''}`}
                    onClick={() => handleTipSelect(20)}
                  >
                    ₹20
                  </button>
                  <button 
                    className={`tip-btn ${tip === 30 ? 'active' : ''}`}
                    onClick={() => handleTipSelect(30)}
                  >
                    ₹30
                  </button>
                  <button 
                    className={`tip-btn ${tip === 50 ? 'active' : ''}`}
                    onClick={() => handleTipSelect(50)}
                  >
                    ₹50
                  </button>
                </div>
                <div className="custom-tip-input">
                  <label>Or enter custom amount:</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Enter amount"
                    value={customTip}
                    onChange={handleCustomTipChange}
                    className="tip-input"
                  />
                </div>
              </div>

              <div className="order-totals">
                <div className="total-row">
                  <span>Subtotal:</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>SGST (2.5%):</span>
                  <span>₹{sgst.toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>CGST (2.5%):</span>
                  <span>₹{cgst.toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>Delivery Charges:</span>
                  <span className="free-delivery">FREE</span>
                </div>
                {tip > 0 && (
                  <div className="total-row tip-row">
                    <span>Tip for Delivery Staff:</span>
                    <span className="tip-amount">₹{tip.toFixed(2)}</span>
                  </div>
                )}
                <div className="total-row total-final">
                  <span>Total:</span>
                  <span>₹{total.toFixed(2)}</span>
                </div>
              </div>
              
              {subtotal < MINIMUM_ORDER_AMOUNT && (
                <div className="minimum-order-notice">
                  <p>⚠️ Minimum order amount is ₹{MINIMUM_ORDER_AMOUNT}</p>
                  <p className="amount-needed">Add ₹{(MINIMUM_ORDER_AMOUNT - subtotal).toFixed(2)} more to place order</p>
                </div>
              )}
              
              <button 
                onClick={handlePlaceOrder}
                className="btn-place-order"
                disabled={loading || subtotal < MINIMUM_ORDER_AMOUNT}
              >
                {loading ? 'Placing Order...' : subtotal < MINIMUM_ORDER_AMOUNT ? `Minimum Order ₹${MINIMUM_ORDER_AMOUNT}` : 'Place Order'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;